import React from 'react';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import { 
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';

const ResettingBehavioursStage3 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 3: Celebrate and Feedback"
        icon={<ArrowPathIcon className="h-7 w-7 text-primary-light" />}
        heading="Monitor progress and celebrate successes"
      >
        <ExpandableCallout
          type="info"
          title="What do I need to DO in this stage?"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 2 things:</p>
            <ol className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1.</span>
                <span className="text-gray-300">Keep an ear to the ground</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2.</span>
                <span className="text-gray-300">Get feedback on any interventions from the last step that you actioned</span>
              </li>
            </ol>
          </div>
        </ExpandableCallout>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-primary-light mr-3" />
            Keep an ear to the ground
          </h2>
          <div className="space-y-6">
            <p className="text-gray-300">
              Over the next few weeks you want to be actively looking out and listening for any resurgence of those anti-behaviours you spotted prior to the town hall.
            </p>
            <p className="text-gray-300">
              You are also looking especially hard for examples of where your true core values and behaviours have shone through.
            </p>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-primary-light mr-3" />
            Celebrate
          </h2>
          <div className="space-y-6">
            <p className="text-gray-300">
              At the next town hall: encourage and celebrate the team. Re-enforce your core values by sharing specific examples of what you've observed that's been different over the last few weeks.
            </p>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Preparation:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Collect Examples: Use the observations and feedback from the past weeks to find examples of core values in action.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Prepare Stories: Write short stories or anecdotes that highlight these positive behaviours.</span>
                </li>
              </ul>
            </div>

            <CopyToNotion title="Town Hall Script">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Town Hall Script:</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                    <p className="text-gray-300 font-semibold mb-2">Opening:</p>
                    <p className="text-gray-300">"Folks it's great to see the change over the past few weeks, just want to share some great examples of how we've been living our core values recently. and give some shoutouts"</p>
                  </div>
                  <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                    <p className="text-gray-300 font-semibold mb-2">Examples:</p>
                    <ul className="space-y-2">
                      <li className="text-gray-300">"For instance, last week, I saw the marketing and sales teams collaborate seamlessly on the new campaign, embodying our value of teamwork."</li>
                      <li className="text-gray-300">"I received feedback that during a critical project, the engineering team stayed transparent about their progress and challenges, showcasing our value of transparency."</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CopyToNotion>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-primary-light mr-3" />
            Get feedback on your interventions
          </h2>
          <div className="space-y-6">
            <p className="text-gray-300">
              If in the previous step you needed to action some systemic underlying causes, it's good to also now check in on these.
            </p>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Questions to ask:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Did the intervention help?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Has it made things better? How?</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Publish the findings to the team:</h3>
              <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                <ol className="space-y-4">
                  <li className="text-gray-300">We've listened to you, you told us xyz was making it hard to embody our values/behaviours abc</li>
                  <li className="text-gray-300">Here's the actions we said we'd take</li>
                  <li className="text-gray-300">Here's what we've completed</li>
                  <li className="text-gray-300">Here's what's next</li>
                  <li className="text-gray-300">Poll - has this made things better</li>
                </ol>
              </div>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <CheckCircleIcon className="h-6 w-6 text-primary-light mr-3" />
            Outcome
          </h2>
          <ul className="space-y-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You've closed the loop by re-enforcing and celebrating the team at the next town hall</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">You've got feedback on any interventions you did action and built trust by saying a thing and following through on it</span>
            </li>
          </ul>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default ResettingBehavioursStage3; 